import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Search, Trash2 } from 'react-feather';
import Table from 'components/shared/table';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import {
  IconButton,
  Skeleton,
  Button,
  Icon,
  Popover,
  ActionList,
} from 'components/ui';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import { REACT_QUERY_STATUS } from 'constants/config';
import {
  useGetReadinessSkills,
  SORT_OPTIONS,
} from '../queries/readinessSkills';
import Layout from '../../Learn/shared/Layout';
import AddCategoryModal from './AddCategoryModal';
import EditCategoryModal from './EditCategoryModal';
import SkillSlider from './SkillSlider';
import MessageBanner from '../MessageBanner';
import SideNavBar from '../SideNavBar';
import DeleteModal from './DeleteModal';
import EmptyState from './LibraryEmptyState';

const CategoryOption = ({
  setAddCategoryTemplateModal,
  setEditCategoryTemplateModal,
}) => {
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);

  return (
    <Popover
      isHighPriority
      rootClassName="d-f"
      className="SkillsLibrary-popover"
      visible={categoryPopoverOpen}
      onClose={() => setCategoryPopoverOpen(false)}
      activator={
        <Text
          onClick={() => setCategoryPopoverOpen(true)}
          variant={TEXT_VARIANTS.BODY}
        >
          {t('skills.manage_categories')}
        </Text>
      }
    >
      <div className="AppSwitcher-popover bdrs-4 f-o-n">
        <ActionList
          items={[
            {
              content: (
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('skills.add_a_category')}
                </Text>
              ),
              onClick: () => {
                setAddCategoryTemplateModal(true);
              },
            },
            {
              content: (
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('skills.edit_category')}
                </Text>
              ),
              onClick: () => {
                setEditCategoryTemplateModal(true);
              },
            },
          ]}
        />
      </div>
    </Popover>
  );
};

const SkillsLibrary = ({ match, location }) => {
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [skillSliderOpen, setSkillSliderOpen] = useState(false);
  const [editSkillSlider, setEditSkillSlider] = useState(false);
  const [chosenSkill, setChosenSkill] = useState({});
  const [bannerProperties, setBannerProperties] = useState({});
  const [addCategoryTemplateModal, setAddCategoryTemplateModal] =
    useState(false);
  const [editCategoryTemplateModal, setEditCategoryTemplateModal] =
    useState(false);
  const [deleteSkill, setDeleteSkill] = useState(false);
  const [skillIdToShowPopoverFor, setSkillIdToShowPopoverFor] = useState(0);
  const [openEditSkillSlider, setOpenEditSkillSlider] = useState(false);

  const closeCategoryModal = () => {
    setAddCategoryTemplateModal(false);
    setEditCategoryTemplateModal(false);
  };
  const [sort, setSort] = useState(SORT_OPTIONS.ASC);

  const page = offset / limit + 1;
  const { data: readinessSkillData, status: readinessSkillsStatus } =
    useGetReadinessSkills({
      limit,
      offset,
      searchTerm,
      sort,
    });

  async function handleDelete() {
    setDeleteSkill(true);
  }

  const closeDeleteModal = () => {
    setDeleteSkill(false);
    setChosenSkill({});
  };

  const isSortingByCategory = sort.includes('category');
  const sortIconName = sort.includes('asc') ? 'triangle_down' : 'triangle_up';
  const skills = readinessSkillData?.readinessSkills?.readinessSkills ?? [];
  const totalCount = readinessSkillData?.readinessSkills?.totalCount;
  const pageCount = Math.ceil(totalCount / limit);

  const tableIsEmpty =
    readinessSkillsStatus === REACT_QUERY_STATUS.SUCCESS && totalCount === 0;
  const tableIsLoading = readinessSkillsStatus === REACT_QUERY_STATUS.LOADING;

  useEffect(() => {
    if (
      readinessSkillsStatus === REACT_QUERY_STATUS.ERROR &&
      totalCount === 0
    ) {
      setBannerProperties({
        message: (
          <p
            dangerouslySetInnerHTML={{
              __html: t('skills.banner_skills_get_error'),
            }}
          />
        ),
        state: 'error',
      });
    }
  }, [readinessSkillsStatus, totalCount]);

  const renderSearch = () => (
    <div>
      <Search className="mr-n-5 c-gray-dark pos-a mt-6 ml-6" />
      <input
        className="SkillsLibrary-searchTermWidth h-36 ff-ss pl-32"
        icon={<Search />}
        placeholder={t('skills.search')}
        onChange={e => setTempSearchTerm(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            setSearchTerm(tempSearchTerm);
            setOffset(0);
          }
        }}
        value={tempSearchTerm}
      />
      {searchTerm && (
        <Icon
          className="ml-neg-24 mr-10 c-gray-dark"
          name="close-circle"
          onClick={() => {
            setSearchTerm('');
            setTempSearchTerm('');
          }}
        />
      )}
    </div>
  );

  return (
    <>
      {!!bannerProperties?.message && (
        <MessageBanner
          message={bannerProperties?.message}
          state={bannerProperties?.state}
          setBannerProperties={setBannerProperties}
        />
      )}
      <Layout.Main>
        <Layout.Side>
          <SideNavBar match={match} location={location} />
        </Layout.Side>
        <Layout.Content>
          <div>
            <div className="Index-header d-f">
              <h1 className="lh-26 fz-20 fw-600 c-brand-night">
                {t('skills.header')}
              </h1>
              <div className="d-f jc-r">
                {renderSearch()}
                <div>
                  {addCategoryTemplateModal && (
                    <AddCategoryModal
                      closeModal={closeCategoryModal}
                      setBannerProperties={setBannerProperties}
                    />
                  )}
                  {editCategoryTemplateModal && (
                    <EditCategoryModal
                      closeModal={closeCategoryModal}
                      setBannerProperties={setBannerProperties}
                    />
                  )}
                  <Popover
                    isHighPriority
                    rootClassName="d-f"
                    visible={popoverOpen}
                    onClose={() => setPopoverOpen(!popoverOpen)}
                    activator={
                      <Button
                        type="button"
                        onClick={() => {
                          setPopoverOpen(!popoverOpen);
                        }}
                        className="Button Button--linked ml-16"
                        icon="add"
                      >
                        {t('skills.add_button')}
                      </Button>
                    }
                  >
                    <div className="AppSwitcher-popover bdrs-4 f-o-n">
                      <ActionList
                        items={[
                          {
                            content: (
                              <Text variant={TEXT_VARIANTS.BODY}>
                                {t('skills.add_skill')}
                              </Text>
                            ),
                            onClick: () => {
                              setSkillSliderOpen(true);
                              setPopoverOpen(false);
                            },
                          },
                          {
                            content: (
                              <CategoryOption
                                setAddCategoryTemplateModal={
                                  setAddCategoryTemplateModal
                                }
                                setEditCategoryTemplateModal={
                                  setEditCategoryTemplateModal
                                }
                              />
                            ),
                            onClick: () => {},
                          },
                        ]}
                      />
                    </div>
                  </Popover>
                </div>
              </div>
            </div>
            <Table
              className="GradingStationTable Index-table--multipleHeaders mb-32 mt-24 bg-white border border-gray-light"
              alternate
              noBorderTop
              roundedBottom
            >
              <Table.Header>
                <Table.Header.Cell
                  className="Table-cell--xLarge Table-cell--clickable Table-cell--sortable"
                  onClick={() => {
                    setSort(
                      sort === SORT_OPTIONS.ASC
                        ? SORT_OPTIONS.DESC
                        : SORT_OPTIONS.ASC,
                    );
                    setOffset(0);
                  }}
                >
                  <div className="d-f fd-r ai-c">
                    <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                      {t('skills.name_column')}
                    </Text>
                    {!isSortingByCategory && (
                      <div className="icon ml-20">
                        <Icon
                          name={sortIconName}
                          className="Icon--small c-brand-gray c-p"
                        />
                      </div>
                    )}
                  </div>
                </Table.Header.Cell>
                <Table.Header.Cell
                  className="Table-cell--small Table-cell--clickable Table-cell--sortable"
                  onClick={() => {
                    setSort(
                      sort === SORT_OPTIONS.CATEGORY_ASC
                        ? SORT_OPTIONS.CATEGORY_DESC
                        : SORT_OPTIONS.CATEGORY_ASC,
                    );
                    setOffset(0);
                  }}
                >
                  <div className="d-f fd-r ai-c">
                    <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                      {t('skills.category_column')}
                    </Text>
                    {isSortingByCategory && (
                      <div className="icon ml-20">
                        <Icon
                          name={sortIconName}
                          className="Icon--small c-brand-gray c-p"
                        />
                      </div>
                    )}
                  </div>
                </Table.Header.Cell>
                <Table.Header.Cell className="Table-cell--xSmall" />
              </Table.Header>
              {tableIsEmpty && (
                <EmptyState
                  openAddSkillSlider={() => setSkillSliderOpen(true)}
                />
              )}
              {tableIsLoading &&
                [...Array(10)].map((e, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Table.Row key={`loading-row-${i}`}>
                    <Table.BodyCell className="Table-cell--xLarge">
                      <Skeleton width={100 * Math.random() + 250} />
                    </Table.BodyCell>
                    <Table.BodyCell className="Table-cell--small">
                      {Math.random() > 0.2 && <Skeleton />}
                    </Table.BodyCell>
                    <Table.BodyCell className="Table-cell--xSmall">
                      <Skeleton width={30} />
                    </Table.BodyCell>
                  </Table.Row>
                ))}
              {!tableIsLoading &&
                !tableIsEmpty &&
                skills?.map(sk => (
                  <Table.Row className="Table-cell--clickable" key={sk.id}>
                    <Table.BodyCell
                      className="Table-cell--xLarge pl-24 border-right-0"
                      key={sk?.id}
                      onClick={() => {
                        setChosenSkill({
                          id: sk.id,
                          name: sk.name,
                          description: sk.description,
                          categoryId: sk.readinessSkillCategory?.id,
                          categoryName: sk.readinessSkillCategory?.name,
                        });
                        setEditSkillSlider(true);
                        setPopoverOpen(false);
                      }}
                    >
                      <Text variant={TEXT_VARIANTS.BODY}>{sk.name}</Text>
                    </Table.BodyCell>
                    <Table.BodyCell
                      className="Table-cell--small pl-24 border-right-0"
                      key={sk?.readinessSkillCategory?.id}
                      onClick={() => {
                        setChosenSkill({
                          id: sk.id,
                          name: sk.name,
                          description: sk.description,
                          categoryId: sk.readinessSkillCategory?.id,
                          categoryName: sk.readinessSkillCategory?.name,
                        });
                        setEditSkillSlider(true);
                        setPopoverOpen(false);
                      }}
                    >
                      <Text variant={TEXT_VARIANTS.BODY}>
                        {sk.readinessSkillCategory?.name}
                      </Text>
                    </Table.BodyCell>
                    <Table.BodyCell className="Table-cell--xSmall pl-1 border-right-0">
                      <Popover
                        className="AppSwitcher-popover bdrs-4 f-o-n"
                        visible={skillIdToShowPopoverFor === sk.id}
                        onClose={() => setSkillIdToShowPopoverFor(0)}
                        activator={
                          <div className="icon ">
                            <IconButton
                              onClick={() => {
                                setChosenSkill({
                                  id: sk.id,
                                  name: sk.name,
                                  description: sk.description,
                                  categoryId: sk.readinessSkillCategory?.id,
                                  categoryName: sk.readinessSkillCategory?.name,
                                });
                                setSkillIdToShowPopoverFor(sk.id);
                                setOpenEditSkillSlider(false);
                                setEditSkillSlider(false);
                              }}
                            >
                              <MoreHorizontal className="Icon--medium c-gray-darker" />
                            </IconButton>
                          </div>
                        }
                      >
                        <ActionList
                          items={[
                            {
                              content: (
                                <>
                                  <Icon
                                    name="edit"
                                    className="d-f ai-c mr-10"
                                  />
                                  <Text variant={TEXT_VARIANTS.BODY}>
                                    {t('skills.edit_skill')}
                                  </Text>
                                </>
                              ),
                              onClick: () => {
                                setChosenSkill({
                                  id: sk.id,
                                  name: sk.name,
                                  description: sk.description,
                                  categoryId: sk.readinessSkillCategory?.id,
                                  categoryName: sk.readinessSkillCategory?.name,
                                });
                                setEditSkillSlider(true);
                                setOpenEditSkillSlider(true);
                                setPopoverOpen(false);
                                setSkillIdToShowPopoverFor(0);
                              },
                            },
                            {
                              content: (
                                <>
                                  <Trash2
                                    name="delete"
                                    size={18}
                                    className="c-red d-f ai-c mr-10"
                                  />
                                  <Text variant={TEXT_VARIANTS.BODY}>
                                    {t('skills.delete_skill')}
                                  </Text>
                                </>
                              ),
                              onClick: () => {
                                handleDelete();
                                setSkillIdToShowPopoverFor(0);
                              },
                            },
                          ]}
                        />
                      </Popover>
                    </Table.BodyCell>
                  </Table.Row>
                ))}
              <Table.Footer className="p-12 border border-gray-light">
                <Table.Pagination
                  onPageChange={e => setOffset((e - 1) * 10)}
                  page={page}
                  pageCount={pageCount}
                  perPage={limit}
                  onPerPageChange={e => {
                    setLimit(e);
                    setOffset(0);
                  }}
                />
              </Table.Footer>
            </Table>
            {deleteSkill && (
              <DeleteModal
                closeDeleteModal={closeDeleteModal}
                formData={chosenSkill}
                setBannerProperties={setBannerProperties}
                deleteSkill={deleteSkill}
              />
            )}
          </div>
        </Layout.Content>
        {(skillSliderOpen || editSkillSlider || openEditSkillSlider) && (
          <SkillSlider
            skillSliderOpen={skillSliderOpen}
            editSkillSlider={editSkillSlider}
            openEditSkillSlider={openEditSkillSlider}
            chosenSkill={chosenSkill}
            setChosenSkill={setChosenSkill}
            bannerProperties={bannerProperties}
            setBannerProperties={setBannerProperties}
            handleClose={() => {
              setSkillSliderOpen(false);
              setEditSkillSlider(false);
              setOpenEditSkillSlider(false);
            }}
          />
        )}
        {/* edit skill slider simply opens the skill slider , skillSliderOpen adds a new skill*/}
        {/* openEditSkillSlider opens skill slider in edit state */}
      </Layout.Main>
    </>
  );
};

export default withSkillsClientWrapper(withReactQueryWrapper(SkillsLibrary));
